/**
 * OTR KPI AI Ruleset Implementation
 * 
 * This file implements the AI diagnostic ruleset for On-Time Running (OTR)
 * data analysis across trips, stops, drivers, vehicles, and depot operations.
 */

import dayjs from '../../../dayjs';

// Constants for analysis
const EARLY_THRESHOLD = -2; // Minutes (negative)
const LATE_THRESHOLD = 5;   // Minutes (positive)
const VERY_LATE_THRESHOLD = 10; // Minutes (positive)

/**
 * Trip Performance Rules Analysis
 */
export const analyzeTripWithRuleset = (trip, historicalData) => {
  if (!trip) return { insight: null, recommendation: null, improvement: null };
  
  // Initialize result
  const result = {
    insight: null,
    recommendation: null,
    improvement: null,
    category: 'trip'
  };
  
  // Check for performance patterns in this trip's stops
  if (trip.recordCount > 0) {
    // Calculate on-time percent for this trip
    const tripOTR = trip.overallStats?.onTimePercent || 0;
    
    // Early Departure Rule
    if (trip.overallStats?.earlyPercent > 30) {
      // Skip this rule if first stop performance is excellent
      if (trip.firstStopStats && trip.firstStopStats.onTimePercent >= 90) {
        // Check if mid stops have early departure issues instead
        if (trip.midStopStats && trip.midStopStats.earlyPercent > 30) {
          result.insight = `Mid-journey early departures (${Math.round(trip.midStopStats.earlyPercent)}% of mid stops)`;
          result.recommendation = "Good first stop performance, but drivers are departing too early from mid-journey stops. Review driver adherence to schedule.";
          result.improvement = Math.round(Math.min(30, trip.midStopStats.earlyPercent - 10));
          return result;
        }
        // Don't flag early departures if first stop performance is excellent and no mid-journey issues
      } else {
        result.insight = `Excessive early departures (${Math.round(trip.overallStats.earlyPercent)}% of stops)`;
        result.recommendation = "Review driver departure procedures and consider schedule adjustment to prevent early departures";
        result.improvement = Math.round(Math.min(30, trip.overallStats.earlyPercent - 10));
        return result;
      }
    }
    
    // Poor First Stop Performance Rule
    if (trip.firstStopStats && trip.firstStopStats.onTimePercent < 60) {
      result.insight = `Poor first stop performance (${Math.round(trip.firstStopStats.onTimePercent)}% on-time)`;
      result.recommendation = "Review trip start procedures, investigate depot departure delays, or adjust schedule start time";
      result.improvement = Math.round(Math.min(25, 90 - trip.firstStopStats.onTimePercent) / 2);
      return result;
    }
    
    // Late Mid-Trip Degradation Rule
    if (trip.midStopStats && trip.lastStopStats && 
        trip.midStopStats.latePercent > 40 && 
        trip.lastStopStats.latePercent > 60) {
      result.insight = `Progressive lateness across the journey (mid: ${Math.round(trip.midStopStats.latePercent)}% late, last: ${Math.round(trip.lastStopStats.latePercent)}% late)`;
      result.recommendation = "Consider adding buffer time between mid and last stops, or review traffic conditions during this trip time";
      result.improvement = Math.round(Math.min(30, (trip.midStopStats.latePercent + trip.lastStopStats.latePercent) / 6));
      return result;
    }
    
    // Overall Poor Performance Rule
    if (tripOTR < 60) {
      result.insight = `Critical overall OTR performance (${Math.round(tripOTR)}% on-time)`;
      result.recommendation = "Complete route timing review required - consider major schedule updates or route changes";
      result.improvement = 15;
      return result;
    }
    
    // Good First Stop but Poor Last Stop Performance Rule
    if (trip.firstStopStats && trip.lastStopStats && 
        trip.firstStopStats.onTimePercent >= 90 && 
        trip.lastStopStats.onTimePercent < 70) {
      
      let insightText = "";
      if (trip.lastStopStats.earlyPercent > trip.lastStopStats.latePercent) {
        insightText = `Journey ends too early (${Math.round(trip.lastStopStats.earlyPercent)}% of last stops)`;
      } else {
        insightText = `Journey ends with delays (${Math.round(trip.lastStopStats.latePercent)}% of last stops)`;
      }
      
      result.insight = insightText;
      result.recommendation = "Good journey start but issues at destination. Review the route's end section timing and traffic conditions.";
      result.improvement = Math.round(Math.min(25, (100 - trip.lastStopStats.onTimePercent) / 3));
      return result;
    }
    
    // Performance Degradation Throughout Journey Rule
    if (trip.firstStopStats && trip.lastStopStats) {
      const performanceDrop = trip.firstStopStats.onTimePercent - trip.lastStopStats.onTimePercent;
      if (performanceDrop > 30) {
        result.insight = `Journey performance degrades by ${Math.round(performanceDrop)}% from start to end`;
        result.recommendation = "Performance deteriorates along the route. Consider adding buffer time or reviewing the schedule's timing distribution.";
        result.improvement = Math.round(Math.min(20, performanceDrop / 3));
        return result;
      }
    }
  }
  
  return result;
};

/**
 * Stop Performance Rules Analysis
 */
export const analyzeStopWithRuleset = (stop, historicalData) => {
  if (!stop) return { insight: null, recommendation: null, improvement: null };
  
  // Initialize result
  const result = {
    insight: null,
    recommendation: null,
    improvement: null,
    category: 'stop'
  };
  
  // Chronic Lateness Rule - Stop is consistently late across most trips
  if (stop.otrPercent < 60 && stop.latePercent > 60) {
    result.insight = `Stop ${stop.tsn} (${stop.stopName}) has chronic lateness issues (${Math.round(stop.latePercent)}% late arrivals)`;
    result.recommendation = "Evaluate if scheduled arrival time is realistic given traffic conditions or stop location constraints";
    result.improvement = Math.round(Math.min(20, (80 - stop.otrPercent) / 3));
    return result;
  }
  
  // Excess Dwell Time Rule - If arrival is on time but departure is late
  if (historicalData && historicalData.length > 0) {
    const stopRecords = historicalData.filter(r => r.TSN === stop.tsn);
    const dwellTimeIssues = stopRecords.filter(r => {
      const arrivalVariance = r.ARRIVAL_TIME_VARIANCE;
      const departureVariance = r.DEPARTURE_TIME_VARIANCE;
      
      // Check if arrival was on time but departure was late
      return (arrivalVariance !== undefined && departureVariance !== undefined &&
              arrivalVariance <= LATE_THRESHOLD && departureVariance > LATE_THRESHOLD &&
              departureVariance - arrivalVariance > 2);
    });
    
    if (dwellTimeIssues.length > 0 && dwellTimeIssues.length / stopRecords.length > 0.3) {
      const dwellPercent = Math.round((dwellTimeIssues.length / stopRecords.length) * 100);
      result.insight = `Stop ${stop.tsn} (${stop.stopName}) has excessive dwell time in ${dwellPercent}% of visits`;
      result.recommendation = "Review passenger boarding/alighting processes, consider bus stop redesign or additional scheduled time at this stop";
      result.improvement = Math.round(Math.min(15, dwellPercent / 3));
      return result;
    }
  }
  
  // Multiple Route Inconsistency
  if (stop.routes && stop.routes.size > 1) {
    // Check if there's wide variance between routes at this stop
    const routePerformance = {};
    let maxDiff = 0;
    let worstRoute = '';
    let bestRoute = '';
    
    // Calculate OTR % for each route
    Object.keys(stop.routeConsistencyStats || {}).forEach(route => {
      const stats = stop.routeConsistencyStats[route];
      if (stats.varianceCount > 0) {
        const onTimePercent = (stats.onTimeCount / stats.varianceCount) * 100;
        routePerformance[route] = onTimePercent;
        
        // Find max difference between routes
        Object.keys(routePerformance).forEach(otherRoute => {
          if (route !== otherRoute) {
            const diff = Math.abs(routePerformance[route] - routePerformance[otherRoute]);
            if (diff > maxDiff) {
              maxDiff = diff;
              if (routePerformance[route] < routePerformance[otherRoute]) {
                worstRoute = route;
                bestRoute = otherRoute;
              } else {
                worstRoute = otherRoute;
                bestRoute = route;
              }
            }
          }
        });
      }
    });
    
    if (maxDiff > 25) {
      result.insight = `Stop ${stop.tsn} has inconsistent performance across routes (${Math.round(maxDiff)}% difference between routes)`;
      result.recommendation = `Review schedule for route ${worstRoute} specifically, which performs worse than ${bestRoute} at this stop`;
      result.improvement = Math.round(Math.min(15, maxDiff / 3));
      return result;
    }
  }
  
  return result;
};

/**
 * Driver Performance Rules Analysis
 */
export const analyzeDriverWithRuleset = (driver, historicalData) => {
  if (!driver) return { insight: null, recommendation: null, improvement: null };
  
  // Initialize result
  const result = {
    insight: null, 
    recommendation: null, 
    improvement: null,
    category: 'driver'
  };
  
  // Excessive Early Departures Rule
  if (driver.overallStats && driver.overallStats.earlyPercent > 30) {
    result.insight = `Driver ${driver.driverId} has excessive early departures (${Math.round(driver.overallStats.earlyPercent)}% of stops)`;
    result.recommendation = "Schedule non-compliance - provide OTR compliance training for driver";
    result.improvement = Math.min(20, Math.round(driver.overallStats.earlyPercent / 3));
    return result;
  }
  
  // Systematically Late Rule
  if (driver.overallStats && driver.overallStats.latePercent > 60) {
    result.insight = `Driver ${driver.driverId} is systematically late (${Math.round(driver.overallStats.latePercent)}% of stops)`;
    result.recommendation = "Investigate whether driver needs additional training or if their assigned routes have unrealistic schedules";
    result.improvement = Math.min(25, Math.round(driver.overallStats.latePercent / 4));
    return result;
  }
  
  // Driver Inconsistency Rule
  if (historicalData && historicalData.length > 0) {
    const driverRecords = historicalData.filter(record => record.DRIVER_ID === driver.driverId);
    
    if (driverRecords.length > 0) {
      // Group by route to check for inconsistency
      const routePerformance = {};
      driverRecords.forEach(record => {
        if (!record.ROUTE) return;
        
        if (!routePerformance[record.ROUTE]) {
          routePerformance[record.ROUTE] = { 
            early: 0, late: 0, onTime: 0, total: 0, 
            variance: record.DEPARTURE_TIME_VARIANCE || record.ARRIVAL_TIME_VARIANCE 
          };
        }
        
        routePerformance[record.ROUTE].total++;
        
        const variance = record.DEPARTURE_TIME_VARIANCE || record.ARRIVAL_TIME_VARIANCE;
        if (variance === undefined || variance === null) return;
        
        if (variance < EARLY_THRESHOLD) routePerformance[record.ROUTE].early++;
        else if (variance > LATE_THRESHOLD) routePerformance[record.ROUTE].late++;
        else routePerformance[record.ROUTE].onTime++;
      });
      
      // Find routes with high variability in performance
      const routes = Object.keys(routePerformance);
      if (routes.length > 1) {
        let highestVar = 0;
        let varRoute = '';
        
        routes.forEach(route => {
          const stats = routePerformance[route];
          if (stats.total > 10) {
            const onTimePercent = (stats.onTime / stats.total) * 100;
            const latePercent = (stats.late / stats.total) * 100;
            const earlyPercent = (stats.early / stats.total) * 100;
            
            // Calculate coefficient of variation as a measure of inconsistency
            const routeMean = (onTimePercent + latePercent + earlyPercent) / 3;
            const routeVar = Math.sqrt(
              Math.pow(onTimePercent - routeMean, 2) +
              Math.pow(latePercent - routeMean, 2) +
              Math.pow(earlyPercent - routeMean, 2)
            ) / routeMean;
            
            if (routeVar > highestVar) {
              highestVar = routeVar;
              varRoute = route;
            }
          }
        });
        
        if (highestVar > 0.8) {
          result.insight = `Driver ${driver.driverId} shows high performance inconsistency, especially on route ${varRoute}`;
          result.recommendation = "Targeted training on specific routes or review of driver scheduling assignments";
          result.improvement = 10;
          return result;
        }
      }
    }
  }
  
  return result;
};

/**
 * Daily Performance Analysis - Finds highest impact insights for a specific date
 */
export const analyzeDailyPerformanceWithRuleset = (dateStr, dateData) => {
  if (!dateData || dateData.length === 0) {
    return { insight: null, recommendation: null, improvement: null };
  }
  
  // First organize data by trip, stop, and driver
  const tripData = {};
  const stopData = {};
  const driverData = {};
  
  // Process records into aggregated structures
  dateData.forEach(record => {
    // Track trip record
    if (record.TRIP_ID) {
      if (!tripData[record.TRIP_ID]) {
        tripData[record.TRIP_ID] = {
          tripId: record.TRIP_ID,
          totalCount: 0,
          earlyCount: 0,
          onTimeCount: 0,
          lateCount: 0,
          firstStopStats: { early: 0, onTime: 0, late: 0, total: 0 },
          midStopStats: { early: 0, onTime: 0, late: 0, total: 0 },
          lastStopStats: { early: 0, onTime: 0, late: 0, total: 0 },
          route: record.ROUTE,
          stops: []
        };
      }
      
      const tripRecord = tripData[record.TRIP_ID];
      tripRecord.totalCount++;
      tripRecord.stops.push(record);
      
      // Calculate on-time status
      const variance = record.DEPARTURE_TIME_VARIANCE || record.ARRIVAL_TIME_VARIANCE;
      if (variance !== undefined && variance !== null) {
        if (variance < EARLY_THRESHOLD) {
          tripRecord.earlyCount++;
          
          // Also track by stop type
          if (record.TSN_FIRST_LAST_IND === 'F') {
            tripRecord.firstStopStats.early++;
            tripRecord.firstStopStats.total++;
          } else if (record.TSN_FIRST_LAST_IND === 'M') {
            tripRecord.midStopStats.early++;
            tripRecord.midStopStats.total++;
          } else if (record.TSN_FIRST_LAST_IND === 'L') {
            tripRecord.lastStopStats.early++;
            tripRecord.lastStopStats.total++;
          }
        } else if (variance > LATE_THRESHOLD) {
          tripRecord.lateCount++;
          
          // Also track by stop type
          if (record.TSN_FIRST_LAST_IND === 'F') {
            tripRecord.firstStopStats.late++;
            tripRecord.firstStopStats.total++;
          } else if (record.TSN_FIRST_LAST_IND === 'M') {
            tripRecord.midStopStats.late++;
            tripRecord.midStopStats.total++;
          } else if (record.TSN_FIRST_LAST_IND === 'L') {
            tripRecord.lastStopStats.late++;
            tripRecord.lastStopStats.total++;
          }
        } else {
          tripRecord.onTimeCount++;
          
          // Also track by stop type
          if (record.TSN_FIRST_LAST_IND === 'F') {
            tripRecord.firstStopStats.onTime++;
            tripRecord.firstStopStats.total++;
          } else if (record.TSN_FIRST_LAST_IND === 'M') {
            tripRecord.midStopStats.onTime++;
            tripRecord.midStopStats.total++;
          } else if (record.TSN_FIRST_LAST_IND === 'L') {
            tripRecord.lastStopStats.onTime++;
            tripRecord.lastStopStats.total++;
          }
        }
      }
    }
    
    // Track stop record
    if (record.TSN) {
      if (!stopData[record.TSN]) {
        stopData[record.TSN] = {
          tsn: record.TSN,
          stopName: record.STOP_NAME || `Stop ${record.TSN}`,
          totalCount: 0,
          earlyCount: 0,
          onTimeCount: 0,
          lateCount: 0,
          routes: new Set(),
          routeConsistencyStats: {},
          drivers: {}
        };
      }
      
      const stopRecord = stopData[record.TSN];
      stopRecord.totalCount++;
      
      // Add route info
      if (record.ROUTE) {
        stopRecord.routes.add(record.ROUTE);
        
        // Track performance by route for consistency analysis
        if (!stopRecord.routeConsistencyStats[record.ROUTE]) {
          stopRecord.routeConsistencyStats[record.ROUTE] = {
            totalVariance: 0,
            varianceCount: 0,
            varianceValues: [],
            earlyCount: 0,
            onTimeCount: 0,
            lateCount: 0
          };
        }
      }
      
      // Track driver at this stop
      if (record.DRIVER_ID) {
        if (!stopRecord.drivers[record.DRIVER_ID]) {
          stopRecord.drivers[record.DRIVER_ID] = { lateCount: 0, totalCount: 0 };
        }
        stopRecord.drivers[record.DRIVER_ID].totalCount++;
      }
      
      // Calculate on-time status
      const variance = record.DEPARTURE_TIME_VARIANCE || record.ARRIVAL_TIME_VARIANCE;
      if (variance !== undefined && variance !== null) {
        if (variance < EARLY_THRESHOLD) {
          stopRecord.earlyCount++;
          if (record.ROUTE) stopRecord.routeConsistencyStats[record.ROUTE].earlyCount++;
        } else if (variance > LATE_THRESHOLD) {
          stopRecord.lateCount++;
          if (record.ROUTE) stopRecord.routeConsistencyStats[record.ROUTE].lateCount++;
          if (record.DRIVER_ID) stopRecord.drivers[record.DRIVER_ID].lateCount++;
        } else {
          stopRecord.onTimeCount++;
          if (record.ROUTE) stopRecord.routeConsistencyStats[record.ROUTE].onTimeCount++;
        }
      }
    }
    
    // Group by driver
    if (record.DRIVER_ID) {
      if (!driverData[record.DRIVER_ID]) {
        driverData[record.DRIVER_ID] = {
          driverId: record.DRIVER_ID,
          totalCount: 0,
          earlyCount: 0,
          onTimeCount: 0,
          lateCount: 0
        };
      }
      
      const driverRecord = driverData[record.DRIVER_ID];
      driverRecord.totalCount++;
      
      // Calculate on-time status
      const variance = record.DEPARTURE_TIME_VARIANCE || record.ARRIVAL_TIME_VARIANCE;
      if (variance !== undefined && variance !== null) {
        if (variance < EARLY_THRESHOLD) {
          driverRecord.earlyCount++;
        } else if (variance > LATE_THRESHOLD) {
          driverRecord.lateCount++;
        } else {
          driverRecord.onTimeCount++;
        }
      }
    }
  });
  
  // Calculate percentages and add to each object
  Object.keys(tripData).forEach(tripId => {
    const trip = tripData[tripId];
    if (trip.totalCount > 0) {
      trip.overallStats = {
        onTimePercent: (trip.onTimeCount / trip.totalCount) * 100,
        earlyPercent: (trip.earlyCount / trip.totalCount) * 100,
        latePercent: (trip.lateCount / trip.totalCount) * 100
      };
      
      // Calculate for each stop type
      if (trip.firstStopStats.total > 0) {
        trip.firstStopStats.onTimePercent = (trip.firstStopStats.onTime / trip.firstStopStats.total) * 100;
        trip.firstStopStats.earlyPercent = (trip.firstStopStats.early / trip.firstStopStats.total) * 100;
        trip.firstStopStats.latePercent = (trip.firstStopStats.late / trip.firstStopStats.total) * 100;
      }
      
      if (trip.midStopStats.total > 0) {
        trip.midStopStats.onTimePercent = (trip.midStopStats.onTime / trip.midStopStats.total) * 100;
        trip.midStopStats.earlyPercent = (trip.midStopStats.early / trip.midStopStats.total) * 100;
        trip.midStopStats.latePercent = (trip.midStopStats.late / trip.midStopStats.total) * 100;
      }
      
      if (trip.lastStopStats.total > 0) {
        trip.lastStopStats.onTimePercent = (trip.lastStopStats.onTime / trip.lastStopStats.total) * 100;
        trip.lastStopStats.earlyPercent = (trip.lastStopStats.early / trip.lastStopStats.total) * 100;
        trip.lastStopStats.latePercent = (trip.lastStopStats.late / trip.lastStopStats.total) * 100;
      }
    }
  });
  
  Object.keys(stopData).forEach(tsn => {
    const stop = stopData[tsn];
    if (stop.totalCount > 0) {
      stop.otrPercent = (stop.onTimeCount / stop.totalCount) * 100;
      stop.earlyPercent = (stop.earlyCount / stop.totalCount) * 100;
      stop.latePercent = (stop.lateCount / stop.totalCount) * 100;
      
      Object.keys(stop.routeConsistencyStats).forEach(route => {
        const routeStats = stop.routeConsistencyStats[route];
        const total = routeStats.earlyCount + routeStats.onTimeCount + routeStats.lateCount;
        if (total > 0) {
          routeStats.onTimePercent = (routeStats.onTimeCount / total) * 100;
          routeStats.earlyPercent = (routeStats.earlyCount / total) * 100;
          routeStats.latePercent = (routeStats.lateCount / total) * 100;
        }
      });
    }
  });
  
  Object.keys(driverData).forEach(driverId => {
    const driver = driverData[driverId];
    if (driver.totalCount > 0) {
      driver.overallStats = {
        onTimePercent: (driver.onTimeCount / driver.totalCount) * 100,
        earlyPercent: (driver.earlyCount / driver.totalCount) * 100,
        latePercent: (driver.lateCount / driver.totalCount) * 100
      };
    }
  });
  
  // Now analyze each object type for insights
  const tripInsights = [];
  const stopInsights = [];
  const driverInsights = [];
  
  // Analyze trips
  Object.values(tripData).forEach(trip => {
    const insight = analyzeTripWithRuleset(trip, dateData);
    if (insight.insight && insight.recommendation) {
      tripInsights.push({...insight, objectId: trip.tripId});
    }
  });
  
  // Analyze stops
  Object.values(stopData).forEach(stop => {
    const insight = analyzeStopWithRuleset(stop, dateData);
    if (insight.insight && insight.recommendation) {
      stopInsights.push({...insight, objectId: stop.tsn});
    }
  });
  
  // Analyze drivers
  Object.values(driverData).forEach(driver => {
    const insight = analyzeDriverWithRuleset(driver, dateData);
    if (insight.insight && insight.recommendation) {
      driverInsights.push({...insight, objectId: driver.driverId});
    }
  });
  
  // Combine all insights and sort by estimated improvement impact
  const allInsights = [
    ...tripInsights.map(insight => ({...insight, sortValue: insight.improvement || 0})),
    ...stopInsights.map(insight => ({...insight, sortValue: insight.improvement || 0})),
    ...driverInsights.map(insight => ({...insight, sortValue: insight.improvement || 0}))
  ].sort((a, b) => b.sortValue - a.sortValue);
  
  // Return the highest impact insight
  if (allInsights.length > 0) {
    return allInsights[0];
  }
  
  return { insight: null, recommendation: null, improvement: null };
}; 